import { StyleSheet, css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Button } from '../../../../../web/components-v2/Button';
import { DateInput } from '../../../../../web/components-v2/DateInput';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../web/components-v2/Dialog';
import { THEME } from '../../../../../web/components-v2/Theme/theme';

const DATE_FORMAT = 'YYYY-MM-DD';

export interface ITextExportDateRange {
	/** Inclusive start of the export window (start of the selected day), or undefined for no lower bound. */
	startDate?: Date;
	/** Inclusive end of the export window (end of the selected day), or undefined for no upper bound. */
	endDate?: Date;
}

interface IProps {
	isOpen: boolean;
	onClose: () => void;
	onExport: (dateRange: ITextExportDateRange) => void;
}

/**
 * Modal that lets a support engineer optionally scope an admin text-message export to a date range.
 *
 * Leaving both dates blank exports the account's entire text history (the previous behavior). Setting a
 * start and/or end date produces a smaller, date-scoped CSV — the backend applies StartDate/EndDate when
 * the export runs with includeAll=false (see reports/texting/export).
 */
export const TextExportDateRangeDialog: React.FC<IProps> = ({ isOpen, onClose, onExport }) => {
	const [startDate, setStartDate] = React.useState('');
	const [endDate, setEndDate] = React.useState('');

	const startMoment = startDate ? moment(startDate, DATE_FORMAT, true) : null;
	const endMoment = endDate ? moment(endDate, DATE_FORMAT, true) : null;

	const startInvalid = !!startDate && !startMoment?.isValid();
	const endInvalid = !!endDate && !endMoment?.isValid();
	const rangeInvalid = !!startMoment?.isValid() && !!endMoment?.isValid() && startMoment.isAfter(endMoment);
	const canExport = !startInvalid && !endInvalid && !rangeInvalid;

	const reset = () => {
		setStartDate('');
		setEndDate('');
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			reset();
			onClose();
		}
	};

	const handleExport = () => {
		if (!canExport) {
			return;
		}
		onExport({
			startDate: startMoment?.isValid() ? startMoment.startOf('day').toDate() : undefined,
			endDate: endMoment?.isValid() ? endMoment.endOf('day').toDate() : undefined,
		});
		reset();
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Export Text Messages</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<p className={css(styleSheet.helperText)}>
						Leave both dates blank to export the account&apos;s entire text history. Set a start and/or end date to
						export a smaller, date-scoped file.
					</p>
					<div className={css(styleSheet.field)}>
						<label className={css(styleSheet.label)}>Start date</label>
						<DateInput value={startDate} onChange={setStartDate} />
					</div>
					<div className={css(styleSheet.field)}>
						<label className={css(styleSheet.label)}>End date</label>
						<DateInput value={endDate} onChange={setEndDate} />
					</div>
					{rangeInvalid ? (
						<p className={css(styleSheet.errorText)}>Start date must be on or before the end date.</p>
					) : null}
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => handleOpenChange(false)}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleExport} disabled={!canExport}>
						Export
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styleSheet = StyleSheet.create({
	helperText: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		marginBottom: THEME.spacing.md,
		marginTop: 0,
	},
	field: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		marginBottom: THEME.spacing.md,
	},
	label: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	errorText: {
		color: THEME.colors.danger.main,
		fontSize: THEME.typography.fontSizes.sm,
		marginBottom: 0,
		marginTop: 0,
	},
});
